import { BrowserCacheLocation } from '@azure/msal-browser';
import { HejAppSwitcherConfig } from './hej-appswitcher-config';
import { HejEventsHandler } from './hej-events-handler';
import { HejMsalConfig } from './hej-msal-config';
import { HejProfileInfoConfig } from './hej-profile-info-config';
import { HejProfileNavigationConfig } from './hej-profile-navigation-config';
import { HejAsyncTranslationProvider, HejJsonTranslationProvider } from './hej-translation-provider';

export class HejComponentConfig {

  private sharepointTenantUrl? = 'https://iweof.sharepoint.com';
  private graphUrl? = 'https://graph.microsoft.com';

  constructor(values?: Partial<HejComponentConfig>) {
    Object.assign(this, values);

    // try to auto populate `protectedResourceScopeMap`
    if (this.msalConfig && !this.msalConfig.protectedResourceScopeMap) {
      this.msalConfig.protectedResourceScopeMap = [];
    }

    // sharepoint
    if (!this.msalConfig.protectedResourceScopeMap.some(p => p[0] === this.sharepointTenantUrl)) {
      this.msalConfig.protectedResourceScopeMap.push([
        this.sharepointTenantUrl,
        [`${this.sharepointTenantUrl}/.default`],
      ]);
    }

    // graph
    if (!this.msalConfig.protectedResourceScopeMap.some(p => p[0] === this.graphUrl)) {
      this.msalConfig.protectedResourceScopeMap.push([
        this.graphUrl,
        [`${this.graphUrl}/.default`],
      ]);
    }

    // gateway
    if (!this.msalConfig.protectedResourceScopeMap.some(p => p[0] === this.apiUrl)) {
      this.msalConfig.protectedResourceScopeMap.push([
        this.apiUrl,
        this.apiScopeList.map(scope => `${this.apiScopePrefix}/${scope}`),
      ]);
    }

    // fallback cache location to msal
    if (this.cacheLocation && this.cacheLocation !== 'memoryStorage' && this.msalConfig?.cache?.cacheLocation) {
      this.cacheLocation = this.msalConfig.cache.cacheLocation as BrowserCacheLocation;
    }

  }

  /** Name of the application, shown in the top left corner. Default is empty string. */
  public name: string;

  /** Your MSAL config */
  public msalConfig?: HejMsalConfig;

  /** Your auth0 config */
  public auth0Config?: {

    /** Your way of getting a token for the given url. Return a token in string form, which will then be attached as header: {Authentication: Bearer <your token>} */
    getToken(url: string): Promise<string>;
  };

  /** Cache location, default is to follow `msalConfig.cache.CacheLocation` */
  public cacheLocation?: BrowserCacheLocation;

  /** Override url to hej. Default: https://hej.ingka.com */
  public hejUrl?: string = 'https://hej.ingka.com';

  /** Root url to the hej api. Default is Hej prod. Only include this if you want to connect to a different environment. */
  public apiUrl?: string = 'https://hej-gateway.ingka.com/api/gateway';

  /** Api scope prefix, e.g. api://D628FD4D-3E08-470B-9872-D1AA4244BCBB. Will get suffixed with /scope. Default is Hej prod scope. Only include this if you want to connect to a different environment. */
  public apiScopePrefix? = 'api://fb00f820-4785-4cdd-817f-c8d2e2ddaf02';

  /** Correponding api scope list, e.g. ['favorite', 'link']. Default is what we use in Hej. Only modify this if you have requested different scopes for your application.  */
  public apiScopeList? = [
    'favorite',
    'link',
    'nowpc',
    'user',
  ];

  /** Translation provider */
  public translationProvider?: HejAsyncTranslationProvider | HejJsonTranslationProvider;

  /** Set to true to show routing changes in browser url and history state api */
  public showLocationChanges?: boolean;

  /** Set this to enable custom profile navigation nodes in profile card */
  public profileNavigationConfig?: HejProfileNavigationConfig;

  /** Config for app switcher */
  public appSwitcherConfig?: HejAppSwitcherConfig;

  /** Set this to enable custom profile fields in profile information card */
  public profileInfoConfig?: HejProfileInfoConfig;

  /** Implement this to handle the logout action */
  public logOutHandler?: (event: Event) => void;

  /** Implement this to handle events in the components. Use this for tracking etc. */
  public events?: HejEventsHandler;

}

/** Name of the window property that contains the hej configuration */
const HEJ_CONFIG_WINDOW_KEY = '__hejConfig';

/** Initialize automatic mode for hej components. This needs to be called before you use any hej components. */
export function initHejComponent(config: Partial<HejComponentConfig>): void {
  // eslint-disable-next-line no-restricted-globals
  if (typeof window === 'undefined') {
    return;
  }

  // eslint-disable-next-line no-restricted-globals
  window[HEJ_CONFIG_WINDOW_KEY] = config;
}

/** Parse hej config from window object */
export function getHejConfig(): HejComponentConfig {
  // eslint-disable-next-line no-restricted-globals
  if (typeof window === 'undefined') {
    return null;
  }

  // eslint-disable-next-line no-restricted-globals
  return new HejComponentConfig(window[HEJ_CONFIG_WINDOW_KEY]);
}

